import React, { useState, useEffect } from 'react';
import { GameEvent, GameEventOption } from '../types';

interface EventModalProps {
    event: GameEvent | null;
    onSelectOption: (option: GameEventOption) => void;
    illustration?: string | null;
}

const EventModal: React.FC<EventModalProps> = ({ event, onSelectOption, illustration }) => {
    const [countdown, setCountdown] = useState(3);
    const [canSelect, setCanSelect] = useState(false);

    // 이벤트가 바뀔 때마다 3초 카운트다운 시작
    useEffect(() => {
        if (!event) return;

        setCountdown(3);
        setCanSelect(false);

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setCanSelect(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [event?.id]);

    if (!event) return null;

    const getCategoryStyle = () => {
        switch (event.category) {
            case 'crisis':
                return 'border-red-500 bg-red-900/20';
            case 'opportunity':
                return 'border-green-500 bg-green-900/20';
            case 'chain':
                return 'border-purple-500 bg-purple-900/20';
            case 'milestone':
                return 'border-yellow-500 bg-yellow-900/20';
            case 'character':
                return 'border-cyan-500 bg-cyan-900/20';
            case 'seasonal':
                return 'border-blue-500 bg-blue-900/20';
            default:
                return 'border-slate-500 bg-slate-900/20';
        }
    };

    const getCategoryIcon = () => {
        switch (event.category) {
            case 'crisis': return '⚠️';
            case 'opportunity': return '✨';
            case 'chain': return '📖';
            case 'milestone': return '🏆';
            case 'character': return '🎭';
            case 'seasonal': return '🌸';
            default: return '📋';
        }
    };

    const getCategoryLabel = () => {
        switch (event.category) {
            case 'crisis': return '위기';
            case 'opportunity': return '기회';
            case 'chain': return '스토리';
            case 'milestone': return '마일스톤';
            case 'character': return '캐릭터';
            case 'seasonal': return '계절';
            default: return '일상';
        }
    };

    const formatChange = (value: number, suffix: string = '') => {
        if (value === 0) return null;
        const sign = value > 0 ? '+' : '';
        const color = value > 0 ? 'text-green-300' : 'text-red-300';
        return <span className={color}>{sign}{value}{suffix}</span>;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
            <div className={`bg-slate-800 w-full max-w-lg rounded-2xl border-4 ${getCategoryStyle()} shadow-2xl overflow-hidden transform transition-all`}>

                {/* Header */}
                <div className={`p-6 text-center border-b ${getCategoryStyle()} relative`}>
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <span className="text-2xl">{getCategoryIcon()}</span>
                        <span className="text-sm text-slate-400 uppercase tracking-wide">
                            {getCategoryLabel()}
                        </span>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2 relative z-10">
                        {event.icon && <span className="mr-2">{event.icon}</span>}
                        {event.title}
                    </h2>
                </div>

                {/* Illustration */}
                {illustration && (
                    <div className="w-full h-72 overflow-hidden relative group">
                        <img
                            src={illustration}
                            alt={event.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-800 via-transparent to-transparent opacity-50"></div>
                    </div>
                )}

                {/* Content */}
                <div className="p-6">
                    <p className="text-slate-200 text-lg leading-relaxed text-center mb-8">
                        {event.description}
                    </p>

                    {/* 카운트다운 표시 */}
                    {!canSelect && (
                        <div className="text-center mb-4 py-2 bg-slate-700 rounded-lg">
                            <span className="text-yellow-400 font-bold animate-pulse">
                                ⏳ 이벤트 확인 중... {countdown}초
                            </span>
                        </div>
                    )}

                    <div className="space-y-3">
                        {event.options.map((option, idx) => (
                            <button
                                key={idx}
                                onClick={() => canSelect && onSelectOption(option)}
                                disabled={!canSelect}
                                className={`w-full group relative p-4 border-2 rounded-xl transition-all duration-200 text-left ${
                                    canSelect
                                        ? 'bg-slate-700 hover:bg-slate-600 border-slate-600 hover:border-slate-500 active:scale-[0.98]'
                                        : 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
                                }`}
                            >
                                <div className="flex justify-between items-start gap-4">
                                    <span className="font-bold text-white group-hover:text-yellow-100 flex-1">
                                        {option.text}
                                    </span>

                                    <div className="flex flex-col items-end gap-1 text-xs shrink-0">
                                        {formatChange(option.moneyChange, ' 🍩')}
                                        {formatChange(option.popChange, ' 🦆')}
                                        {formatChange(option.happinessChange, '% 😊')}
                                    </div>
                                </div>

                                {/* Special indicators */}
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {option.triggersChain && (
                                        <span className="text-xs bg-purple-600/50 text-purple-200 px-2 py-0.5 rounded">
                                            📖 스토리 시작
                                        </span>
                                    )}
                                    {option.unlockCharacter && (
                                        <span className="text-xs bg-cyan-600/50 text-cyan-200 px-2 py-0.5 rounded">
                                            🎭 새 캐릭터
                                        </span>
                                    )}
                                    {option.setFlag && (
                                        <span className="text-xs bg-yellow-600/50 text-yellow-200 px-2 py-0.5 rounded">
                                            ⭐ 특별 효과
                                        </span>
                                    )}
                                    {option.triggersEnding && (
                                        <span className="text-xs bg-red-600/50 text-red-200 px-2 py-0.5 rounded animate-pulse">
                                            🔚 엔딩
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chain Progress (if applicable) */}
                {event.chainId && event.chainStep !== undefined && (
                    <div className="px-6 pb-4">
                        <div className="bg-slate-900 rounded-lg p-3">
                            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                                <span>스토리 진행</span>
                                <span>{event.chainStep + 1} / ?</span>
                            </div>
                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-purple-500 transition-all duration-500"
                                    style={{ width: `${Math.min(100, (event.chainStep + 1) * 15)}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventModal;
